import * as React from 'react';
import moment from 'moment';
import { rgba } from 'polished';
import styled, { css } from 'styled-components';

import { type BlogPost } from '../../templates/blogPostTemplate';
import { INTERNAL_LINKS } from '../../utils';
import { Heading, Text } from '../design-system';
import { LinkWithQuery } from '../global/LinkWithQuery';
import { SocialShare } from '../global/SocialShare';

import { BlogPostHeroMedia } from './BlogPostHeroMedia';

const Wrapper = styled.div<{ $isInterview: boolean }>`
  display: flex;
  flex-direction: column;
  margin: 0 auto 50px;
  width: 100%;
  max-width: calc(${({ theme }) => theme.maxWidth.BLOG} + 270px);

  ${({ $isInterview }) =>
    $isInterview &&
    css`
      flex-direction: row;
      margin-bottom: 26px;
    `}

  @media (max-width: ${({ theme }) => theme.mediaQuery.MOBILE}) {
    flex-direction: column;
    margin-bottom: -50px;
  }
`;

const Info = styled.div<{ $isInterview: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  max-width: 770px;
  margin: 0 auto;
  width: 100%;
  padding: 0 40px;

  ${({ $isInterview }) =>
    $isInterview &&
    css`
      margin-right: 50px;
    `}

  @media (max-width: ${({ theme }) => theme.mediaQuery.MOBILE}) {
    margin: 0 0 30px;

    h1 {
      margin-bottom: 20px;
    }
  }
`;

const Synopsis = styled(Text).attrs({ size: 'extra-large' })`
  margin-bottom: 30px;
  border-bottom: 1px solid ${({ theme }) => theme.borderColor.DEFAULT};
  padding-bottom: 25px;
`;

const CategoryTag = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid ${({ theme }) => rgba(theme.colors.LIGHT_PURPLE, 0.3)};
  padding: 0 6px;
  height: 26px;
`;

const FriendsOfCategory = styled.div`
  border: 1px solid ${({ theme }) => theme.borderColor.DEFAULT};
  font-size: 24px;
  font-weight: 600;
  padding: 11px 12px 12px;
  margin-bottom: 26px;
`;

const MetaData = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  font-size: ${({ theme }) => theme.fontSize.MEDIUM};

  > div {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
  }
`;

interface BlogPostHeaderProps {
  categories: string[];
  post: BlogPost;
}

export const BlogPostHeader: React.ComponentType<BlogPostHeaderProps> =
  React.memo(({ post, categories }) => {
    return (
      <Wrapper $isInterview={post.interview}>
        <Info $isInterview={post.interview}>
          <Heading
            css={`
              margin-bottom: 26px;
            `}
            renderAs="h6">
            <LinkWithQuery to={INTERNAL_LINKS.Blog}>Blog</LinkWithQuery>
          </Heading>
          {post.interview && (
            <FriendsOfCategory>Friends of data</FriendsOfCategory>
          )}
          <Heading
            css={`
              margin-bottom: 26px;
            `}
            renderAs="h1">
            {post.title}
          </Heading>
          <Synopsis>{post.synopsis}</Synopsis>
          <MetaData>
            <div>
              <div>{post.author}</div>
              {!post.interview &&
                categories.map(category => (
                  <CategoryTag key={category}>{category}</CategoryTag>
                ))}
              <div>{moment(post.date).format('MMMM D, YYYY')}</div>
            </div>
            <SocialShare title={post.title} />
          </MetaData>
        </Info>
        <BlogPostHeroMedia post={post} />
      </Wrapper>
    );
  });
